import { createApi } from "@reduxjs/toolkit/query/react";
import type {
	AccessGroup,
	AccessGroupAPI,
	AccessGroupObject,
} from "../../service/types/AccessGroup";
import type { User, UserApiResponseType } from "../../service/types/UserTypes";
import type {
	DlmCompanyConfig,
	CompanyMart,
	PbiAppInstallation,
	SieValidateResponse,
	DlmLegalEntityMartConfig,
	DlmDataObjectTypes,
	DlmRawMartConfig,
	TemplateApp,
	DatabaseUser,
	DatabaseConnection,
	CompanyStatusInfo,
	DestinationTimestamp,
	FleetManifestationTimestamps,
} from "./exopenApiTypes";
import type { Company, CompanyInfo } from "../../service/companies";
import type { ReportStatus } from "../../service/ewr";
import { mapApiUserToUser } from "../../service/users";
import { makeBaseQueryWithUnauthentication } from "./baseQuery";
import { serviceUrl } from "../../../env";

export const exopenApi = createApi({
	reducerPath: "exopenApi",
	baseQuery: makeBaseQueryWithUnauthentication({ baseUrl: serviceUrl }),
	tagTypes: [
		"Company",
		"EWR",
		"User",
		"AccountHierarchy",
		"AccessGroup",
		"CompanyDataLake",
		"Journal",
		"JournalMetadata",
		"ExchangeStrategies",
		"LegalEntityCurrency",
		"LegalEntityAccount",
		"CurrentUser",
		"CompanyMarts",
		"SystemUser",
		"DataLayerStatus",
		"CompanyDomainStatus",
		"DestinationTimestamps",
		"ApiKey",
	],
	endpoints: (builder) => ({
		getAvailableObjectsForAccessGroups: builder.query<
			AccessGroupObject[],
			string
		>({
			query: (companyId) =>
				`api/AccessGroups/AvailableObjectsForAccessGroups/${companyId}`,
		}),

		getAccessGroups: builder.query<AccessGroup[], string>({
			query: (companyId) => `api/AccessGroups/GetAccessGroups/${companyId}`,
			transformResponse: (response: AccessGroupAPI[]) => {
				return response.map((accessGroup) => {
					const legalEntities: string[] = [];

					if (typeof accessGroup.settings.legalEntities === "string") {
						try {
							const parsedLegalEntities = JSON.parse(
								accessGroup.settings.legalEntities,
							);
							if (Array.isArray(parsedLegalEntities)) {
								for (const element of parsedLegalEntities) {
									if (typeof element === "string") {
										legalEntities.push(element);
									}
								}
							}
						} catch {
							// ignore invalid json
						}
					}

					return {
						...accessGroup,
						description:
							typeof accessGroup.settings.description === "string"
								? accessGroup.settings.description
								: "",
						colorCode:
							typeof accessGroup.settings.colorCode === "string"
								? accessGroup.settings.colorCode
								: "",
						legalEntities,
					};
				});
			},
			providesTags: (result) =>
				result
					? [
							...result.map(({ groupId }) => ({
								type: "User" as const,
								id: groupId,
							})),
							"AccessGroup",
						]
					: ["AccessGroup"],
		}),

		deleteAccessGroup: builder.mutation<
			void,
			{ companyId: string; groupId: number }
		>({
			query({ companyId, groupId }) {
				return {
					method: "DELETE",
					url: `api/AccessGroups/RemoveAccessGroup/${companyId}/${groupId}`,
					responseHandler: "text",
				};
			},
			invalidatesTags: ["AccessGroup"],
		}),

		addAccessGroup: builder.mutation<
			void,
			{
				companyId: string;
				name: string;
				description: string;
				colorCode: string;
				accessGroupObjects: { objectId: string }[];
				legalEntities: string[];
			}
		>({
			query({
				companyId,
				name,
				description,
				colorCode,
				legalEntities,
				accessGroupObjects,
			}) {
				return {
					url: `api/AccessGroups/AddAccessGroup/${companyId}`,
					method: "POST",
					body: {
						name,
						settings: {
							description,
							colorCode,
							legalEntities: JSON.stringify(legalEntities),
						},
						accessGroupObjects,
					},
					responseHandler: "text",
				};
			},
			invalidatesTags: ["AccessGroup"],
		}),

		editAccessGroup: builder.mutation<
			void,
			{
				companyId: string;
				groupId: number;
				name: string;
				description: string;
				colorCode: string;
				accessGroupObjects: { objectId: string }[];
				legalEntities: string[];
			}
		>({
			query({
				accessGroupObjects,
				colorCode,
				companyId,
				legalEntities,
				description,
				groupId,
				name,
			}) {
				return {
					url: `api/AccessGroups/UpdateAccessGroup/${companyId}/${groupId}`,
					method: "PUT",
					body: {
						name,
						settings: {
							description,
							colorCode,
							legalEntities: JSON.stringify(legalEntities),
						},
						accessGroupObjects,
					},
					responseHandler: "text",
				};
			},
			invalidatesTags: ["AccessGroup"],
		}),

		getCompanyUsers: builder.query<User[], string>({
			query: (companyId) => `User/companies/${companyId}`,
			transformResponse(response: UserApiResponseType[]) {
				return response.map(mapApiUserToUser);
			},
			providesTags: (result) => {
				return result
					? [
							...result.flatMap(({ email, accessGroups }) => {
								return [
									{ type: "User" as const, id: email },
									...accessGroups.map((accessGroup) => {
										return {
											type: "AccessGroup" as const,
											id: accessGroup.groupId,
										};
									}),
								];
							}),
							"User",
						]
					: ["User"];
			},
		}),

		updateUser: builder.mutation<
			void,
			{ companyId: string; email: string; accessGroups: number[] }
		>({
			query: ({ companyId, accessGroups, email }) => {
				return {
					url: `api/AccessGroups/UpdateAccessGroupsOfUser/${companyId}`,
					method: "PUT",
					body: {
						userEmail: email,
						accessGroupIdList: accessGroups,
					},
					responseHandler: "text",
				};
			},
			invalidatesTags: ["User"],
		}),

		deleteUser: builder.mutation<void, { companyId: string; email: string }>({
			query({ companyId, email }) {
				return {
					url: "User/RemoveUserFromCompany",
					method: "PUT",
					body: {
						userEmail: email,
						companyId: Number(companyId),
					},
				};
			},
			invalidatesTags: ["User"],
		}),

		inviteUser: builder.mutation<
			void,
			{ companyId: string; email: string; accessGroups: number[] }
		>({
			query({ companyId, email, accessGroups }) {
				return {
					url: "Invitation/invite",
					method: "POST",
					body: {
						emailAddress: email,
						accessGroupIds: accessGroups,
						companyId: Number(companyId),
					},
				};
			},
			invalidatesTags: ["User"],
		}),

		validateSieFile: builder.mutation<
			SieValidateResponse,
			{ companyId: string; file: Blob }
		>({
			query({ companyId, file }) {
				const body = new FormData();
				body.append("File", file);
				body.append("CompanyId", companyId);

				return {
					method: "POST",
					url: "api/v2/sie/validate",
					body,
				};
			},
		}),

		approveSieFile: builder.mutation<
			void,
			{
				sieId: string;
				companyId: string;
				companyNameFromSie: string;
				organizationNumberFromSie: string;
				runDataLoadOnApprove?: boolean;
			}
		>({
			query({
				companyId,
				companyNameFromSie,
				organizationNumberFromSie,
				sieId,
				runDataLoadOnApprove = true,
			}) {
				return {
					method: "POST",
					url: "api/v2/sie/approve",
					body: {
						companyId,
						sieId,
						CompanyNameFromSie: companyNameFromSie,
						OrganizationNumberFromSie: organizationNumberFromSie,
					},
					params: { runDataLoadOnApprove },
				};
			},
		}),

		deleteSiePeriod: builder.mutation<
			void,
			{
				companyId: string;
				fiscalYearEntityId: string;
			}
		>({
			query({ companyId, fiscalYearEntityId }) {
				return {
					method: "DELETE",
					url: `sie/v1/${companyId}/periods/${fiscalYearEntityId}`,
				};
			},
		}),

		getAllCompanies: builder.query<Company[], void>({
			query() {
				return "api/v2/companies";
			},
			providesTags: ["Company"],
		}),

		getCompany: builder.query<CompanyInfo, { companyId: string }>({
			query({ companyId }) {
				return `api/v2/companies/${companyId}`;
			},
			providesTags: ["EWR", "Company"],
		}),

		deleteCompany: builder.mutation<void, { companyId: string }>({
			query({ companyId }) {
				return {
					method: "DELETE",
					url: `api/v2/companies/${companyId}`,
				};
			},
			invalidatesTags: ["Company"],
		}),

		createCompany: builder.mutation<
			{ id: number },
			{ name: string; type: string }
		>({
			query({ name, type }) {
				return {
					url: "api/v3/companies",
					method: "POST",
					params: { blocking: true },
					body: {
						name,
						type,
					},
				};
			},
			invalidatesTags: ["Company"],
		}),

		getCurrentUser: builder.query<User, void>({
			query: () => "User/CurrentUser",
			transformResponse(response: UserApiResponseType) {
				return mapApiUserToUser(response);
			},
			providesTags: () => {
				return ["CurrentUser"];
			},
		}),

		getCompanyMarts: builder.query<CompanyMart[], string>({
			query: (companyId) => {
				return `api/Marts/companies/${companyId}/allandactive`;
			},
			providesTags: () => {
				return ["CompanyMarts"];
			},
		}),
		setActiveMarts: builder.mutation<
			void,
			{ companyId: string; martIds: number[] }
		>({
			query({ companyId, martIds }) {
				return {
					method: "POST",
					url: "/api/Marts/setactive",
					body: { CompanyId: companyId, SetAsActiveMart: martIds },
					responseHandler: "text",
				};
			},
			invalidatesTags: ["CompanyMarts"],
		}),

		getSimpleUsers: builder.query<
			{ name: string; email: string }[],
			{ companyId: string }
		>({
			query({ companyId }) {
				return `User/companies/${companyId}/assignableusers`;
			},
		}),

		createReportDatabase: builder.mutation<void, { companyDomainId: string }>({
			query({ companyDomainId }) {
				return {
					method: "POST",
					url: `/report/v1/${companyDomainId}/deploy`,
				};
			},
			invalidatesTags: ["EWR"],
		}),

		createReportEwr: builder.mutation<void, { companyDomainId: string }>({
			query({ companyDomainId }) {
				return {
					method: "POST",
					url: `/ewr/v2/${companyDomainId}/report/ewr`,
				};
			},
			invalidatesTags: ["EWR"],
		}),

		// Which report-database modules are actually deployed for this company
		// (introspected server-side, not the EwrPackages catalog above).
		getReportStatus: builder.query<ReportStatus, { companyDomainId: string }>({
			query({ companyDomainId }) {
				return `report/v1/${companyDomainId}`;
			},
			providesTags: ["EWR"],
		}),

		getSystemUsers: builder.query<User[], void>({
			query() {
				return { url: "User/users" };
			},
			providesTags: ["SystemUser"],
		}),

		deleteUserFromSystem: builder.mutation<void, { email: string }>({
			query({ email }) {
				return {
					url: "User/user",
					headers: {
						userEmail: email,
					},
					method: "DELETE",
				};
			},
			invalidatesTags: ["SystemUser"],
		}),

		addUserToCompany: builder.mutation<
			void,
			{ companyDomainId: string; email: string }
		>({
			query({ email, companyDomainId }) {
				return {
					method: "PUT",
					url: "User/Companies",
					body: {
						userEmail: email,
						companyId: Number(companyDomainId),
					},
				};
			},
			invalidatesTags: ["User"],
		}),

		changeCompanyType: builder.mutation<
			void,
			{ companyDomainId: string; type: string }
		>({
			query({ companyDomainId, type }) {
				return {
					method: "PUT",
					url: `api/v2/companies/${companyDomainId}/type`,
					body: {
						type,
					},
				};
			},
			invalidatesTags: ["Company"],
		}),

		getCompanyConfigDlm: builder.query<
			DlmCompanyConfig,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return `api/v1/datalayer/manifestation/${companyDomainId}/config`;
			},
		}),

		getDlmDataObjectTypes: builder.query<DlmDataObjectTypes[], void>({
			query() {
				return "api/v1/datalayer/manifestation/data-object-types";
			},
		}),

		getDlmRawSystems: builder.query<string[], void>({
			query() {
				return "api/v1/datalayer/manifestation/available-raw-systems";
			},
		}),

		setCompanyDefaultDestinationDlm: builder.mutation<
			void,
			{
				companyDomainId: string;
				rawMartEnabled: boolean;
				financeMartEnabled: boolean;
				currencyMartEnabled: boolean;
			}
		>({
			query({
				companyDomainId,
				financeMartEnabled,
				rawMartEnabled,
				currencyMartEnabled,
			}) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/config/default`,
					body: {
						financeMartEnabled,
						rawMartEnabled,
						currencyMartEnabled,
					},
				};
			},
		}),

		deleteCompanyDestinationDlm: builder.mutation<
			void,
			{
				companyDomainId: string;
			}
		>({
			query({ companyDomainId }) {
				return {
					method: "DELETE",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/config`,
				};
			},
		}),

		getFleetManifestationTimestamps: builder.query<
			FleetManifestationTimestamps[],
			void
		>({
			query() {
				return {
					method: "GET",
					url: "api/v1/datalayer/manifestation/timestamps",
				};
			},
			providesTags: [{ type: "DestinationTimestamps", id: "FLEET" }],
		}),
		getDestinationTimestamps: builder.query<
			DestinationTimestamp[],
			{
				companyDomainId: string;
			}
		>({
			query({ companyDomainId }) {
				return {
					method: "GET",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/timestamps`,
				};
			},
			providesTags: (_result, _error, arg) => [
				{
					type: "DestinationTimestamps",
					id: arg.companyDomainId,
				},
			],
		}),

		setLegalEntityConfigurationDlm: builder.mutation<
			void,
			{
				companyDomainId: string;
				legalEntityMartSettings: DlmLegalEntityMartConfig[];
			}
		>({
			query({ companyDomainId, legalEntityMartSettings }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/set-marts`,
					body: legalEntityMartSettings,
				};
			},
		}),

		setIntegrationRawConfigurationDlm: builder.mutation<
			void,
			{
				companyDomainId: string;
				integrationRawMartSettings: DlmRawMartConfig[];
			}
		>({
			query({ companyDomainId, integrationRawMartSettings }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/set-raw-integrations`,
					body: integrationRawMartSettings,
				};
			},
		}),

		runFullLoadDlmFinanceForCompanyDomain: builder.mutation<
			void,
			{
				companyDomainId: string;
				dataObjectType?: string | undefined;
			}
		>({
			query({ companyDomainId, dataObjectType }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/finance/full-load`,
					params: { dataObjectType },
				};
			},
		}),

		runFullLoadDlmRawForCompanyDomain: builder.mutation<
			void,
			{
				companyDomainId: string;
				dataObjectType?: string | undefined;
			}
		>({
			query({ companyDomainId, dataObjectType }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/raw/full-load`,
					params: { dataObjectType },
				};
			},
		}),

		runFullLoadDlmFinanceForLegalEntity: builder.mutation<
			void,
			{
				companyDomainId: string;
				legalEntityId: string;
				dataObjectType?: string | undefined;
			}
		>({
			query({ companyDomainId, legalEntityId, dataObjectType }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/finance/full-load/${legalEntityId}`,
					params: { dataObjectType },
				};
			},
		}),

		runFullLoadDlmRawForIntegration: builder.mutation<
			void,
			{
				companyDomainId: string;
				integrationId: number;
				dataObjectType?: string | undefined;
			}
		>({
			query({ companyDomainId, integrationId, dataObjectType }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/raw/full-load/${integrationId}`,
					params: { dataObjectType },
				};
			},
		}),

		runFullLoadDlmRawForSingleSystem: builder.mutation<
			void,
			{
				companyDomainId: string;
				system: string;
			}
		>({
			query({ companyDomainId, system }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/raw/full-load/system/${system}`,
				};
			},
		}),

		resetDatabaseDlmFinance: builder.mutation<
			void,
			{
				companyDomainId: string;
			}
		>({
			query({ companyDomainId }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/finance/reset-db`,
				};
			},
		}),

		resetDatabaseDlmRaw: builder.mutation<
			void,
			{
				companyDomainId: string;
				system: string;
			}
		>({
			query({ companyDomainId, system }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/raw/reset-db/${system}`,
				};
			},
		}),

		createMissingFinanceTablesDlm: builder.mutation<
			void,
			{
				companyDomainId: string;
			}
		>({
			query({ companyDomainId }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/finance/create-missing-tables`,
				};
			},
		}),

		createMissingRawTablesDlm: builder.mutation<
			void,
			{
				companyDomainId: string;
				system: string;
			}
		>({
			query({ companyDomainId, system }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/raw/create-missing-tables/${system}`,
				};
			},
		}),

		setDlmSchemaPrefix: builder.mutation<
			void,
			{
				companyDomainId: string;
				prefix: string;
			}
		>({
			query({ companyDomainId, prefix }) {
				return {
					method: "POST",
					url: `api/v1/datalayer/manifestation/${companyDomainId}/db-schema-prefix`,
					params: { prefix },
				};
			},
		}),

		getTemplateApps: builder.query<TemplateApp[], void>({
			query() {
				return "api/v1/datalayer/pbi-template-app";
			},
		}),
		getTemplateAppByName: builder.query<
			PbiAppInstallation,
			{
				companyDomainId: string;
				appName: "ExopenFinancialReportTemplate";
			}
		>({
			query({ companyDomainId, appName }) {
				return `api/v1/datalayer/${companyDomainId}/pbi/template-app/${appName}?dataStorageLocation=Database`;
			},
		}),

		putTemplateApps: builder.mutation<void, { templateApp: TemplateApp }>({
			query({ templateApp }) {
				return {
					method: "PUT",
					url: `api/v1/datalayer/pbi-template-app/${templateApp.name}`,
					body: {
						appId: templateApp.appId,
						packageKey: templateApp.packageKey,
						ownerTenantId: templateApp.ownerTenantId,
					},
				};
			},
		}),

		getPublicFile: builder.query<Blob, { fileName: string }>({
			query({ fileName }) {
				return {
					url: `Storage/public/${fileName}`,
					responseHandler: (response) => response.blob(),
				};
			},
		}),

		getDatabaseParameters: builder.query<
			DatabaseConnection,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return `api/v1/datalayer/${companyDomainId}/pbi/database-parameters`;
			},
		}),

		getDatabaseReaderUser: builder.query<
			DatabaseUser,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return `api/v1/datalayer/${companyDomainId}/database-reader-user`;
			},
		}),

		resetDatabaseReaderUser: builder.mutation<
			DatabaseUser,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return {
					method: "PUT",
					url: `api/v1/datalayer/${companyDomainId}/database-reader-user/generate-password`,
				};
			},
		}),

		getCompanyDomainStatus: builder.query<
			CompanyStatusInfo,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return `api/v2/companies/${companyDomainId}`;
			},
			providesTags: ["CompanyDomainStatus"],
		}),

		provisionCompanyDomainDatabase: builder.mutation<
			void,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return {
					method: "POST",
					url: `/api/v3/companies/${companyDomainId}/database`,
				};
			},
			invalidatesTags: ["CompanyDomainStatus"],
		}),

		enableCompanyDomainDatabaseBackup: builder.mutation<
			void,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return {
					method: "POST",
					url: `/api/v3/companies/${companyDomainId}/database/enable-backup`,
				};
			},
			invalidatesTags: ["CompanyDomainStatus"],
		}),

		disableCompanyDomainDatabaseBackup: builder.mutation<
			void,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return {
					method: "POST",
					url: `/api/v3/companies/${companyDomainId}/database/disable-backup`,
				};
			},
			invalidatesTags: ["CompanyDomainStatus"],
		}),

		provisionCompanyDomainStorageAccount: builder.mutation<
			void,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return {
					method: "POST",
					url: `/api/v3/companies/${companyDomainId}/storage-account`,
				};
			},
			invalidatesTags: ["CompanyDomainStatus"],
		}),

		deleteCompanyDomainStorageAccount: builder.mutation<
			void,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return {
					method: "DELETE",
					url: `/api/v3/companies/${companyDomainId}/storage-account`,
				};
			},
			invalidatesTags: ["CompanyDomainStatus"],
		}),

		provisionCompanyDomainDataFactory: builder.mutation<
			void,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return {
					method: "POST",
					url: `/api/v3/companies/${companyDomainId}/data-factory`,
				};
			},
			invalidatesTags: ["CompanyDomainStatus"],
		}),

		deleteCompanyDomainDataFactory: builder.mutation<
			void,
			{ companyDomainId: string }
		>({
			query({ companyDomainId }) {
				return {
					method: "DELETE",
					url: `/api/v3/companies/${companyDomainId}/data-factory`,
				};
			},
			invalidatesTags: ["CompanyDomainStatus"],
		}),
	}),
});

export const {
	useGetAvailableObjectsForAccessGroupsQuery,
	useGetAccessGroupsQuery,
	useUpdateUserMutation,
	useDeleteUserMutation,
	useInviteUserMutation,
	useGetCompanyUsersQuery,
	useValidateSieFileMutation,
	useApproveSieFileMutation,
	useDeleteSiePeriodMutation,
	useGetCompanyQuery,
	useCreateCompanyMutation,
	useGetCurrentUserQuery,
	useGetSimpleUsersQuery,
	useSetActiveMartsMutation,
	useGetCompanyMartsQuery,
	useDeleteAccessGroupMutation,
	useAddAccessGroupMutation,
	useEditAccessGroupMutation,
	useDeleteCompanyMutation,
	useCreateReportDatabaseMutation,
	useCreateReportEwrMutation,
	useGetReportStatusQuery,
	useDeleteUserFromSystemMutation,
	useGetSystemUsersQuery,
	useAddUserToCompanyMutation,
	useChangeCompanyTypeMutation,
	useGetCompanyConfigDlmQuery,
	useGetDlmDataObjectTypesQuery,
	useGetDlmRawSystemsQuery,
	useSetCompanyDefaultDestinationDlmMutation,
	useDeleteCompanyDestinationDlmMutation,
	useGetDestinationTimestampsQuery,
	useGetFleetManifestationTimestampsQuery,
	useSetLegalEntityConfigurationDlmMutation,
	useSetIntegrationRawConfigurationDlmMutation,
	useRunFullLoadDlmFinanceForCompanyDomainMutation,
	useRunFullLoadDlmRawForCompanyDomainMutation,
	useRunFullLoadDlmFinanceForLegalEntityMutation,
	useRunFullLoadDlmRawForIntegrationMutation,
	useRunFullLoadDlmRawForSingleSystemMutation,
	useResetDatabaseDlmFinanceMutation,
	useResetDatabaseDlmRawMutation,
	useCreateMissingFinanceTablesDlmMutation,
	useCreateMissingRawTablesDlmMutation,
	useSetDlmSchemaPrefixMutation,
	useGetTemplateAppsQuery,
	usePutTemplateAppsMutation,
	useLazyGetPublicFileQuery,
	useLazyGetDatabaseReaderUserQuery,
	useGetDatabaseReaderUserQuery,
	useResetDatabaseReaderUserMutation,
	useGetDatabaseParametersQuery,
	useGetTemplateAppByNameQuery,
	useGetCompanyDomainStatusQuery,
	useProvisionCompanyDomainDatabaseMutation,
	useEnableCompanyDomainDatabaseBackupMutation,
	useDisableCompanyDomainDatabaseBackupMutation,
	useProvisionCompanyDomainStorageAccountMutation,
	useDeleteCompanyDomainStorageAccountMutation,
	useProvisionCompanyDomainDataFactoryMutation,
	useDeleteCompanyDomainDataFactoryMutation,
} = exopenApi;
